import { createReducer } from 'redux-ramda'
import { always, path, pathOr, merge, assoc } from 'ramda'
import { createAction } from 'redux-actions'

import { angularActionNameBuilder } from './dispatchAngular'
import { ACTION_INIT } from './commonActions'
import { defaultCustomColors } from './customColors'
import { getKey, isCustomEmbedByKey, getSubtype } from '~/util/customEmbed'

export const ACTION_SETTINGS_LOADED = angularActionNameBuilder(
  'settings_loaded'
)
export const setSettings = createAction(ACTION_SETTINGS_LOADED)

export const ACTION_FEATURED_MEDIA_SETTING = 'ellipsis.settings.featuredMedia'
export const applyFeaturedMedia = createAction(ACTION_FEATURED_MEDIA_SETTING)

export const ACTION_DROPCAPS_SETTING = 'ellipsis.settings.dropcaps'
export const applyDropcaps = createAction(ACTION_DROPCAPS_SETTING)

export const ACTION_UPDATE_CREATE_ANNOTATION =
  'ellipsis.settings.updateCreateAnnotation'
export const updateCreateAnnotation = createAction(
  ACTION_UPDATE_CREATE_ANNOTATION
)

export const ACTION_DELETE_ANNOTATION = 'ellipsis.settings.deleteAnnotation'
export const deleteAnnotation = createAction(ACTION_DELETE_ANNOTATION)

export const ACTION_UPDATE_CUSTOM_EMBED = 'ellipsis.settings.updateCustomEmbed'
export const updateCustomEmbed = createAction(ACTION_UPDATE_CUSTOM_EMBED)

export const ACTION_DELETE_CUSTOM_EMBED = 'ellipsis.settings.deleteCustomEmbed'
export const deleteCustomEmbed = createAction(ACTION_DELETE_CUSTOM_EMBED)

// Ellipsis websked
export const ACTION_WEBSKED_DISABLED = 'ellipsis.app.webskedDisabled'
export const setWebskedDisabled = createAction(ACTION_WEBSKED_DISABLED)
export const getWebskedDisabled = path([
  'settings',
  'disableWebskedIntegration',
])

export const getLineSettings = path(['settings', 'lineSettings'])

const initialState = {
  customColors: defaultCustomColors,
  lineSettings: {
    unit: null,
    value: null,
    linesPerInch: null,
  },
  linkListSubtypes: [],
  linkSubtypes: [],
  annotations: [],
}

export default createReducer(initialState, [
  [ACTION_INIT, () => always(initialState)],
  [
    ACTION_SETTINGS_LOADED,
    (payload) => always(merge(initialState, payload.data)),
  ],
  [ACTION_FEATURED_MEDIA_SETTING, assoc('featuredMedia')],
  [ACTION_DROPCAPS_SETTING, assoc('dropCaps')],
  [
    ACTION_UPDATE_CUSTOM_EMBED,
    (payload) => (state) => {
      const { embed, subtype, icon } = payload

      return {
        ...state,
        customEmbed: {
          ...state.customEmbed,
          [subtype]: {
            ...embed,
            icon,
          },
        },
        tools: state.tools.includes(getKey(subtype))
          ? state.tools
          : [getKey(subtype), ...state.tools],
      }
    },
  ],
  [
    ACTION_UPDATE_CREATE_ANNOTATION,
    (annotation) => (state) => {
      const newAnnotations = [...state.annotations]
      const index = state.annotations.findIndex(
        (element) => element.id === annotation.id
      )
      if (index !== -1) {
        newAnnotations[index] = { ...annotation }
      } else {
        newAnnotations.push(annotation)
      }
      return {
        ...state,
        annotations: [...newAnnotations],
      }
    },
  ],
  [
    ACTION_DELETE_ANNOTATION,
    (annotation) => (state) => {
      const newAnnotations = [...state.annotations]
      const index = state.annotations.findIndex(
        (element) => element.id === annotation.id
      )
      if (index !== -1) {
        newAnnotations.splice(index, 1)
      }
      return {
        ...state,
        annotations: [...newAnnotations],
      }
    },
  ],
  [
    ACTION_DELETE_CUSTOM_EMBED,
    (key) => (state) => {
      const subtype = isCustomEmbedByKey(key) ? getSubtype(key) : key
      const {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        [subtype]: removedCustomEmbed,
        ...remainingCustomEmbeds
      } = state.customEmbed

      return {
        ...state,
        customEmbed: remainingCustomEmbeds,
        tools: state.tools.filter((toolType) => toolType !== key),
      }
    },
  ],
])

export const getSettings = path(['settings'])
export const getSiteServiceHierarchy = path([
  'settings',
  'siteServiceHierarchy',
])
export const getRemoveEmptyParagraphs = pathOr(false, [
  'settings',
  'removeEmptyParagraphs',
])
export const getTansaSettings = pathOr({}, [
  'settings',
  'proofreaders',
  'tansa',
])
export const getMediaOptions = pathOr({}, ['settings', 'mediaOptions'])
export const permitTabsUnsaved = pathOr(false, [
  'settings',
  'permitTabsUnsaved',
])
export const disabledPlugins = pathOr({}, ['settings', 'disabledDraftPlugins'])
export const siteServiceBasePaths = pathOr(null, [
  'settings',
  'siteServiceBasePathsV2',
])
export const displaySendToPrint = pathOr(false, [
  'settings',
  'displaySendToPrint',
])
export const getCustomColors = pathOr(defaultCustomColors, [
  'settings',
  'customColors',
])
export const getFeaturedMedia = path(['settings', 'featuredMedia'])
export const getCustomEmbed = pathOr({}, ['settings', 'customEmbed'])
export const getDropCaps = pathOr(false, ['settings', 'dropCaps'])
